import { useCallback, useState } from 'react';

import { LocalStorageKey } from './useLocalStorageProxyState.types';

function useLocalStorageProxyState<ValueType extends Record<string, any>>(
  key: LocalStorageKey,
  initialValue: ValueType
) {
  const getStoredValue = useCallback((): ValueType | null => {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.log(error);
      return null;
    }
  }, [key]);

  const [value, setStoredValue] = useState<ValueType>(() => {
    return getStoredValue() ?? initialValue;
  });

  const localStorageProxyHandler: ProxyHandler<ValueType> = {
    get: useCallback(
      (target: ValueType, targetKey: string) => {
        const storedValue = getStoredValue();

        return storedValue !== null
          ? storedValue?.[targetKey]
          : value?.[targetKey];
      },
      [getStoredValue, value]
    ),

    ownKeys: () => {
      const storedValue = getStoredValue();

      const keys =
        storedValue !== null ? Object.keys(storedValue) : Object.keys(value);

      return Array.from(new Set(keys));
    },

    has: (target, targetKey) => {
      const storedValue = getStoredValue();

      return storedValue !== null
        ? targetKey in storedValue
        : targetKey in value;
    },

    getOwnPropertyDescriptor: (target, targetKey) => {
      if (typeof targetKey === 'symbol') {
        return undefined;
      }

      const storedValue = getStoredValue();
      const computedValue = storedValue !== null ? storedValue : value;

      if (targetKey in computedValue) {
        return {
          enumerable: true,
          configurable: true,
          value: computedValue[targetKey]
        };
      }

      return undefined;
    }
  };

  const localStorageProxy = new Proxy(
    {} as ValueType,
    localStorageProxyHandler
  );

  const setValue = (
    newValue: ValueType,
    replacer?: (this: any, key: string, value: any) => any
  ) => {
    setStoredValue(newValue);

    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(newValue, replacer));
      }
    } catch (error) {
      console.log(error);
    }
  };

  return [localStorageProxy, setValue] as const;
}

export default useLocalStorageProxyState;
